import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const NewsCard = ({ article, onExpand, isExpanded }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'ประกาศ': 'bg-primary text-primary-foreground',
      'ข่าวสาร': 'bg-secondary text-secondary-foreground',
      'กิจกรรม': 'bg-accent text-accent-foreground',
      'บริการ': 'bg-success text-success-foreground',
      'เตือนภัย': 'bg-warning text-warning-foreground',
      'ด่วน': 'bg-error text-error-foreground'
    };
    return colors?.[category] || 'bg-muted text-muted-foreground';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'สูง':
        return 'AlertTriangle';
      case 'กลาง':
        return 'Info';
      default:
        return 'FileText';
    }
  };

  const publishDate = article?.publish_date || article?.created_at || article?.publishDate;

  // หารูปภาพที่จะแสดง (จาก media_urls หรือ image_url)
  let displayImage = null;
  if (article?.media_urls) {
    try {
      const mediaUrls = typeof article.media_urls === 'string' 
        ? JSON.parse(article.media_urls) 
        : (Array.isArray(article.media_urls) ? article.media_urls : []);
      const firstImage = mediaUrls.find(m => m.type === 'image');
      if (firstImage) {
        displayImage = firstImage.url;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  if (!displayImage) {
    displayImage = article?.image || article?.image_url;
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-government hover:shadow-government-lg transition-government">
      {/* Article Image */}
      {displayImage && (
        <div className="relative h-48 overflow-hidden rounded-t-lg">
          <Image
            src={displayImage}
            alt={article?.title}
            className={`w-full h-full object-cover transition-government ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
              <Icon name="Image" size={32} className="text-muted-foreground" />
            </div>
          )}
          
          {/* Priority Badge */}
          {article?.priority === 'สูง' && (
            <div className="absolute top-3 right-3 bg-error text-error-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
              <Icon name={getPriorityIcon(article?.priority)} size={12} />
              <span>ด่วน</span>
            </div>
          )}
        </div>
      )}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(article?.category)}`}>
              {article?.category}
            </span>
            {article?.priority !== 'ปกติ' && (
              <Icon 
                name={getPriorityIcon(article?.priority)} 
                size={16} 
                className={article?.priority === 'สูง' ? 'text-error' : 'text-warning'}
              />
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {publishDate ? formatDate(publishDate) : ''}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-foreground mb-3 line-clamp-2">
          <Link to={`/news/${article?.id}`} className="hover:underline">
            {article?.title}
          </Link>
        </h3>

        {/* Summary */}
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {article?.summary}
        </p>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-border pt-4 mb-4">
            <div className="prose prose-sm max-w-none text-foreground">
              {article?.content?.split('\n')?.map((paragraph, index) => (
                <p key={index} className="mb-3 text-sm leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
            
            {/* Article Meta */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="User" size={14} />
                  <span>โดย: {article?.author}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Eye" size={14} />
                  <span>อ่าน: {article?.views?.toLocaleString('th-TH')} ครั้ง</span>
                </div>
                {article?.tags && article?.tags?.length > 0 && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Tag" size={14} />
                    <span>{article?.tags?.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Link
            to={`/news/${article?.id}`}
            className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-government text-sm font-medium"
          >
            <Icon name="ChevronRight" size={16} />
            <span>อ่านเพิ่มเติม</span>
          </Link>

          <div className="flex items-center space-x-2">
            <button
              className="p-2 hover:bg-muted rounded-md transition-government"
              title="แชร์บทความ"
            >
              <Icon name="Share2" size={16} className="text-muted-foreground" />
            </button>
            <button
              className="p-2 hover:bg-muted rounded-md transition-government"
              title="บันทึกบทความ"
            >
              <Icon name="Bookmark" size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsCard;